// EventPhotographyGallery.js - Event Photography Portfolio Gallery component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates an event photography portfolio gallery with image grid and dynamic styling
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the gallery is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The gallery elements
 */
export const createEventPhotographyGalleryElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const gallerySectionClass = 'event-gallery-section';
  const galleryContainerClass = 'event-gallery-container';
  const galleryHeaderClass = 'event-gallery-header';
  const galleryTitleClass = 'event-gallery-title';
  const gallerySubtitleClass = 'event-gallery-subtitle';
  const galleryGridClass = 'event-gallery-grid';
  const galleryImageItemClass = 'event-gallery-image-item';
  const galleryImageClass = 'event-gallery-image';
  const galleryImageOverlayClass = 'event-gallery-image-overlay';
  const galleryImageInfoClass = 'event-gallery-image-info';
  const galleryImageTitleClass = 'event-gallery-image-title';
  const galleryImageEventTypeClass = 'event-gallery-image-event-type';
  const galleryPatternOverlayClass = 'event-gallery-pattern-overlay';

  // Desktop Styles - Event theme with vibrant design
  const gallerySectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#0a0a0a',
    background: `
      radial-gradient(circle at 25% 25%, rgba(236, 72, 153, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.06) 0%, transparent 50%),
      linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%, #0a0a0a 100%)
    `,
    color: '#f8fafc',
    width: '100%',
    overflow: 'hidden'
  };

  const galleryPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      radial-gradient(circle at 25% 25%, rgba(236, 72, 153, 0.03) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.03) 1px, transparent 1px)
    `,
    backgroundSize: '80px 80px, 120px 120px',
    backgroundPosition: '0 0, 60px 60px',
    zIndex: '0',
    opacity: '0.4'
  };

  const galleryContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const galleryHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const galleryTitleStyles = {
    fontSize: '56px',
    fontWeight: '300',
    color: '#f8fafc',
    marginBottom: '24px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '2px',
    lineHeight: '1.1',
    position: 'relative'
  };

  const gallerySubtitleStyles = {
    fontSize: '20px',
    color: 'rgba(248, 250, 252, 0.8)',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300'
  };

  const galleryGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px',
    marginTop: '40px'
  };

  const galleryImageItemStyles = {
    position: 'relative',
    height: '420px',
    borderRadius: '16px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    background: 'rgba(26, 26, 26, 0.8)',
    border: '1px solid rgba(236, 72, 153, 0.15)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
  };

  const galleryImageItemHoverStyles = {
    transform: 'translateY(-12px) scale(1.03)',
    border: '1px solid rgba(236, 72, 153, 0.4)',
    boxShadow: `
      0 32px 64px rgba(0, 0, 0, 0.4),
      0 0 40px rgba(236, 72, 153, 0.2)
    `
  };

  const galleryImageStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'all 0.5s ease',
    filter: 'brightness(0.9) contrast(1.1) saturate(1.1)'
  };

  const galleryImageHoverStyles = {
    filter: 'brightness(0.7) contrast(1.2) saturate(1.2)',
    transform: 'scale(1.08)'
  };

  const galleryImageOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: `
      linear-gradient(135deg, 
        rgba(236, 72, 153, 0.15) 0%, 
        rgba(59, 130, 246, 0.1) 40%, 
        rgba(10, 10, 10, 0.8) 100%
      )
    `,
    opacity: '0',
    transition: 'all 0.5s ease',
    zIndex: '1'
  };

  const galleryImageOverlayHoverStyles = {
    opacity: '1'
  };

  const galleryImageInfoStyles = {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    padding: '30px 25px',
    zIndex: '2',
    transform: 'translateY(20px)',
    opacity: '0',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const galleryImageInfoHoverStyles = {
    transform: 'translateY(0)',
    opacity: '1'
  };

  const galleryImageTitleStyles = {
    fontSize: '22px',
    fontWeight: '400',
    color: '#ffffff',
    marginBottom: '8px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '1px',
    lineHeight: '1.2'
  };

  const galleryImageEventTypeStyles = {
    fontSize: '12px',
    color: '#ec4899',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  // Tablet Styles
  const galleryTabletStyles = {
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const galleryTitleTabletStyles = {
    fontSize: '48px'
  };

  const gallerySubtitleTabletStyles = {
    fontSize: '18px'
  };

  const galleryGridTabletStyles = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '25px'
  };

  const galleryImageItemTabletStyles = {
    height: '360px'
  };

  // Mobile Styles
  const galleryMobileStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const galleryTitleMobileStyles = {
    fontSize: '40px'
  };

  const gallerySubtitleMobileStyles = {
    fontSize: '16px'
  };

  const galleryGridMobileStyles = {
    gridTemplateColumns: '1fr',
    gap: '20px'
  };

  const galleryImageItemMobileStyles = {
    height: '300px'
  };

  const galleryImageInfoMobileStyles = {
    padding: '25px 20px'
  };

  const galleryImageTitleMobileStyles = {
    fontSize: '20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(gallerySectionClass, gallerySectionStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryPatternOverlayClass, galleryPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryContainerClass, galleryContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryHeaderClass, galleryHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTitleClass, galleryTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(gallerySubtitleClass, gallerySubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryGridClass, galleryGridStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageItemClass, galleryImageItemStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageClass, galleryImageStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageOverlayClass, galleryImageOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageInfoClass, galleryImageInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageTitleClass, galleryImageTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageEventTypeClass, galleryImageEventTypeStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageItemClass}--hover`, galleryImageItemHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageClass}--hover`, galleryImageHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageOverlayClass}--hover`, galleryImageOverlayHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageInfoClass}--hover`, galleryImageInfoHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(gallerySectionClass, galleryTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryTitleClass, galleryTitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(gallerySubtitleClass, gallerySubtitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryGridClass, galleryGridTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryImageItemClass, galleryImageItemTabletStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(gallerySectionClass, galleryMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryTitleClass, galleryTitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(gallerySubtitleClass, gallerySubtitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryGridClass, galleryGridMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageItemClass, galleryImageItemMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageInfoClass, galleryImageInfoMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageTitleClass, galleryImageTitleMobileStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    gallerySectionClass,
    galleryPatternOverlayClass,
    galleryContainerClass,
    galleryHeaderClass,
    galleryTitleClass,
    gallerySubtitleClass,
    galleryGridClass,
    galleryImageItemClass,
    galleryImageClass,
    galleryImageOverlayClass,
    galleryImageInfoClass,
    galleryImageTitleClass,
    galleryImageEventTypeClass
  ]));

  // Event photography images data
  const eventImages = [
    {
      src: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=600&h=400&fit=crop&crop=center',
      title: 'Corporate Gala Night',
      eventType: 'Corporate Event'
    },
    {
      src: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=600&h=400&fit=crop&crop=center',
      title: 'Birthday Celebration',
      eventType: 'Private Party'
    },
    {
      src: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&h=400&fit=crop&crop=center',
      title: 'Conference Keynote',
      eventType: 'Business Conference'
    },
    {
      src: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=600&h=400&fit=crop&crop=center',
      title: 'Anniversary Celebration',
      eventType: 'Milestone Event'
    },
    {
      src: 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=600&h=400&fit=crop&crop=center',
      title: 'Product Launch Event',
      eventType: 'Corporate Launch'
    },
    {
      src: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=600&h=400&fit=crop&crop=center',
      title: 'Family Reunion',
      eventType: 'Family Gathering'
    },
    {
      src: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&h=400&fit=crop&crop=center',
      title: 'Music Festival',
      eventType: 'Entertainment Event'
    },
    {
      src: 'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=600&h=400&fit=crop&crop=center',
      title: 'Charity Fundraiser',
      eventType: 'Non-Profit Event'
    },
    {
      src: 'https://images.unsplash.com/photo-1556125574-d7f27ec36a06?w=600&h=400&fit=crop&crop=center',
      title: 'Award Ceremony',
      eventType: 'Recognition Event'
    },
    {
      src: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=600&h=400&fit=crop&crop=center',
      title: 'Graduation Party',
      eventType: 'Academic Celebration'
    },
    {
      src: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=600&h=400&fit=crop&crop=center',
      title: 'Trade Show Booth',
      eventType: 'Exhibition Event'
    },
    {
      src: 'https://images.unsplash.com/photo-1528605105345-5344ea20e269?w=600&h=400&fit=crop&crop=center',
      title: 'Holiday Party',
      eventType: 'Seasonal Celebration'
    }
  ];

  // Generate IDs for elements
  const sectionId = Date.now();
  const patternOverlayId = Date.now() + 1;
  const containerId = Date.now() + 2;
  const headerId = Date.now() + 3;
  const titleId = Date.now() + 4;
  const subtitleId = Date.now() + 5;
  const gridId = Date.now() + 6;

  // Create image elements
  let currentId = gridId + 1;
  const imageElements = [];
  const imageIds = [];

  eventImages.forEach((image, index) => {
    const itemId = currentId++;
    const imageId = currentId++;
    const overlayId = currentId++;
    const infoId = currentId++;
    const titleId = currentId++;
    const eventTypeId = currentId++;

    imageIds.push(itemId);

    imageElements.push(
      // Image Item Container
      {
        id: itemId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Event Image Item ${index + 1}`,
        parentId: gridId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageItemClass,
        styles: {},
        children: [imageId, overlayId, infoId],
        text: '',
        attributes: {
          'data-element': 'event-gallery-image-item',
          'data-index': index.toString()
        }
      },
      // Image
      {
        id: imageId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'image',
        name: `Event Image ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('image'),
        className: galleryImageClass,
        styles: {},
        src: image.src,
        attributes: {
          'data-element': 'event-gallery-image',
          'alt': image.title
        }
      },
      // Overlay
      {
        id: overlayId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Event Image Overlay ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageOverlayClass,
        styles: {},
        children: [],
        text: '',
        attributes: {
          'data-element': 'event-gallery-image-overlay'
        }
      },
      // Info
      {
        id: infoId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Event Image Info ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageInfoClass,
        styles: {},
        children: [titleId, eventTypeId],
        text: '',
        attributes: {
          'data-element': 'event-gallery-image-info'
        }
      },
      // Title
      {
        id: titleId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'h4',
        name: `Event Image Title ${index + 1}`,
        parentId: infoId,
        hierarchyName: getHierarchyName('h4'),
        className: galleryImageTitleClass,
        styles: {},
        children: [],
        text: image.title,
        attributes: {
          'data-element': 'event-gallery-image-title'
        }
      },
      // Event Type
      {
        id: eventTypeId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Event Image Type ${index + 1}`,
        parentId: infoId,
        hierarchyName: getHierarchyName('span'),
        className: galleryImageEventTypeClass,
        styles: {},
        children: [],
        text: image.eventType,
        attributes: {
          'data-element': 'event-gallery-image-event-type'
        }
      }
    );
  });

  // Create all elements
  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Event Photography Gallery Section',
      parentId: null,
      className: gallerySectionClass,
      styles: {},
      children: [patternOverlayId, containerId],
      text: '',
      attributes: {
        'data-element': 'event-gallery-section',
        'data-component': 'section',
        'data-section-type': 'event-gallery',
        'data-page-id': currentPageId
      }
    },
    // Pattern Overlay
    {
      id: patternOverlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Event Gallery Pattern Overlay',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'event-gallery-pattern-overlay'
      }
    },
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Event Gallery Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryContainerClass,
      styles: {},
      children: [headerId, gridId],
      text: '',
      attributes: {
        'data-element': 'event-gallery-container'
      }
    },
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Event Gallery Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'event-gallery-header'
      }
    },
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Event Gallery Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: galleryTitleClass,
      styles: {},
      children: [],
      text: 'Event Photography',
      attributes: {
        'data-element': 'event-gallery-title'
      }
    },
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Event Gallery Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: gallerySubtitleClass,
      styles: {},
      children: [],
      text: 'Capturing the energy, excitement, and memorable moments of your special events. From corporate gatherings to private celebrations, every moment matters.',
      attributes: {
        'data-element': 'event-gallery-subtitle'
      }
    },
    // Grid
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Event Gallery Grid',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryGridClass,
      styles: {},
      children: imageIds,
      text: '',
      attributes: {
        'data-element': 'event-gallery-grid'
      }
    },
    ...imageElements
  ];

  return {
    eventGallerySection: newElements[0],
    elements: newElements
  };
}; 